import React, { useCallback, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { countries } from '../data/countries';
import { useDismiss } from '../hooks/useDismiss';
import { useVisitorCountry } from '../hooks/useVisitorCountry';
const ease = [0.22, 1, 0.36, 1] as const;
type CountrySelectProps = {
  className?: string;
};

// Footer Country/Region picker — pre-set to where the visitor is browsing from
export function CountrySelect({
  className = ''
}: CountrySelectProps) {
  const [country, setCountry] = useVisitorCountry();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);
  const current = countries.find(c => c.iso === country) ?? countries[0];
  return <div ref={ref} className="relative inline-block" data-id="element-298" data-mp="2qu6y2kil612q">
      <button type="button" aria-haspopup="listbox" aria-expanded={open} aria-label={`Country/Region: ${current.name}`} onClick={() => setOpen(o => !o)} className={`inline-flex items-center gap-1.5 ${className}`} data-id="element-299" data-mp="2qvrpw2din5ww">
        {current.name}
        <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-300 ease-luxe ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} aria-hidden="true" data-id="element-300" data-mp="11ncum7zek7d7" />
      </button>

      <AnimatePresence data-id="element-301" data-mp="1bdpncnhdf6uf">
        {open && <motion.ul role="listbox" aria-label="Country/Region" initial={{
        opacity: 0,
        y: 4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 4
      }} transition={{
        duration: 0.22,
        ease
      }} className="absolute bottom-full left-0 z-30 mb-3 max-h-[320px] w-[280px] overflow-y-auto border border-paper/15 bg-ink py-2 text-paper shadow-[0_16px_40px_-16px_rgba(0,0,0,0.6)]" data-id="element-302" data-mp="1gcj0gesqwvu2">
            {countries.map(c => {
          const selected = c.iso === country;
          return <li key={c.iso} role="option" aria-selected={selected} data-id="element-303" data-mp="tbihpyv6ah21">
                  <button type="button" onClick={() => {
              setCountry(c.iso);
              setOpen(false);
            }} className={`flex w-full items-center gap-3 px-5 py-2.5 text-left text-[14px] transition-colors duration-200 hover:bg-paper/10 ${selected ? 'font-medium' : 'font-light text-paper/80'}`} data-id="element-304" data-mp="tdd1vayme552">
                    <span className="flex-1" data-id="element-305" data-mp="1qy93uod7svrw">{c.name}</span>
                    {selected && <CheckIcon className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-306" data-mp="1lzo2glm8861v" />}
                  </button>
                </li>;
        })}
          </motion.ul>}
      </AnimatePresence>
    </div>;
}